import { useState, useMemo } from 'react';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import {
  useTaxRates,
  useCreateTaxRate,
  useUpdateTaxRate,
  useDeleteTaxRate,
  type TaxRate,
  type CreateTaxRatePayload,
  type UpdateTaxRatePayload,
} from './hooks/useTaxRates';

/**
 * Canadian provinces/territories for the province selector.
 */
const PROVINCE_OPTIONS = [
  { value: '', label: 'All / None' },
  { value: 'AB', label: 'Alberta (AB)' },
  { value: 'BC', label: 'British Columbia (BC)' },
  { value: 'MB', label: 'Manitoba (MB)' },
  { value: 'NB', label: 'New Brunswick (NB)' },
  { value: 'NL', label: 'Newfoundland & Labrador (NL)' },
  { value: 'NS', label: 'Nova Scotia (NS)' },
  { value: 'NT', label: 'Northwest Territories (NT)' },
  { value: 'NU', label: 'Nunavut (NU)' },
  { value: 'ON', label: 'Ontario (ON)' },
  { value: 'PE', label: 'Prince Edward Island (PE)' },
  { value: 'QC', label: 'Quebec (QC)' },
  { value: 'SK', label: 'Saskatchewan (SK)' },
  { value: 'YT', label: 'Yukon (YT)' },
];

/**
 * TaxRatesPage — CRUD management page for configuring region-to-rate tax mappings.
 * Supports creating, editing, setting defaults, and deactivating tax rates.
 *
 * Requirements: 15.22
 */
export function TaxRatesPage() {
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingRate, setEditingRate] = useState<TaxRate | null>(null);

  const { data: taxRates, isLoading } = useTaxRates();
  const createMutation = useCreateTaxRate();
  const updateMutation = useUpdateTaxRate();
  const deleteMutation = useDeleteTaxRate();

  const sortedRates = useMemo(
    () => [...(taxRates ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [taxRates],
  );

  const activeCount = sortedRates.filter((r) => r.isActive).length;

  const handleCreate = () => {
    setEditingRate(null);
    setIsEditorOpen(true);
  };

  const handleEdit = (taxRate: TaxRate) => {
    setEditingRate(taxRate);
    setIsEditorOpen(true);
  };

  const handleSave = (data: CreateTaxRatePayload | UpdateTaxRatePayload) => {
    if (editingRate) {
      updateMutation.mutate(
        { id: editingRate.id, data: data as UpdateTaxRatePayload },
        {
          onSuccess: () => {
            setIsEditorOpen(false);
            setEditingRate(null);
          },
        },
      );
    } else {
      createMutation.mutate(data as CreateTaxRatePayload, {
        onSuccess: () => {
          setIsEditorOpen(false);
          setEditingRate(null);
        },
      });
    }
  };

  const handleToggleDefault = (taxRate: TaxRate) => {
    updateMutation.mutate({
      id: taxRate.id,
      data: { is_default: !taxRate.isDefault },
    });
  };

  const handleToggleActive = (taxRate: TaxRate) => {
    if (taxRate.isActive) {
      deleteMutation.mutate(taxRate.id);
    } else {
      updateMutation.mutate({ id: taxRate.id, data: { is_active: true } });
    }
  };

  if (isLoading) {
    return (
      <Card>
        <div className="flex items-center justify-center py-12">
          <div
            className="h-8 w-8 animate-spin rounded-full border-4 border-brand-600 border-t-transparent"
            role="status"
            aria-label="Loading tax rates"
          />
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Tax Rates"
          description="Configure region-to-rate tax mappings (GST, HST, PST by province). Default rates are auto-applied to new invoices and quotes."
          action={
            <Button size="sm" onClick={handleCreate}>
              Add Tax Rate
            </Button>
          }
        />
        <CardContent>
          {sortedRates.length === 0 ? (
            <p className="py-4 text-sm text-gray-500 dark:text-gray-400">
              No tax rates configured yet. Add your first tax rate to get started.
            </p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" role="table">
                  <thead>
                    <tr className="border-b border-border text-left dark:border-border-dark">
                      <th className="pb-3 pr-4 font-medium text-gray-500 dark:text-gray-400">
                        Name
                      </th>
                      <th className="pb-3 pr-4 font-medium text-gray-500 dark:text-gray-400">
                        Rate %
                      </th>
                      <th className="pb-3 pr-4 font-medium text-gray-500 dark:text-gray-400">
                        Province
                      </th>
                      <th className="pb-3 pr-4 font-medium text-gray-500 dark:text-gray-400">
                        Default
                      </th>
                      <th className="pb-3 pr-4 font-medium text-gray-500 dark:text-gray-400">
                        Status
                      </th>
                      <th className="pb-3 font-medium text-gray-500 dark:text-gray-400">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border dark:divide-border-dark">
                    {sortedRates.map((taxRate) => (
                      <tr key={taxRate.id}>
                        <td className="py-3 pr-4 font-medium text-gray-900 dark:text-gray-100">
                          {taxRate.name}
                        </td>
                        <td className="py-3 pr-4 text-gray-600 dark:text-gray-300">
                          {formatRatePercent(taxRate.rate)}
                        </td>
                        <td className="py-3 pr-4 text-gray-600 dark:text-gray-300">
                          {taxRate.province || 'All'}
                        </td>
                        <td className="py-3 pr-4">
                          <button
                            type="button"
                            onClick={() => handleToggleDefault(taxRate)}
                            className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded"
                            aria-label={`${taxRate.isDefault ? 'Remove default' : 'Set as default'} for ${taxRate.name}`}
                          >
                            <Badge variant={taxRate.isDefault ? 'success' : 'default'} size="sm">
                              {taxRate.isDefault ? 'Default' : 'Set Default'}
                            </Badge>
                          </button>
                        </td>
                        <td className="py-3 pr-4">
                          <Badge variant={taxRate.isActive ? 'success' : 'default'} size="sm" dot>
                            {taxRate.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td className="py-3">
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="sm" onClick={() => handleEdit(taxRate)}>
                              Edit
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleToggleActive(taxRate)}
                            >
                              {taxRate.isActive ? 'Deactivate' : 'Activate'}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                {activeCount} active tax rate{activeCount !== 1 ? 's' : ''}
              </p>
            </>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      {isEditorOpen && (
        <TaxRateEditorModal
          taxRate={editingRate}
          onSave={handleSave}
          onClose={() => {
            setIsEditorOpen(false);
            setEditingRate(null);
          }}
          isSaving={createMutation.isPending || updateMutation.isPending}
        />
      )}
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatRatePercent(rate: number): string {
  return `${(rate * 100).toFixed(2)}%`;
}

// ─── Tax Rate Editor Modal ────────────────────────────────────────────────────

interface TaxRateEditorModalProps {
  taxRate: TaxRate | null;
  onSave: (data: CreateTaxRatePayload | UpdateTaxRatePayload) => void;
  onClose: () => void;
  isSaving: boolean;
}

function TaxRateEditorModal({ taxRate, onSave, onClose, isSaving }: TaxRateEditorModalProps) {
  const [form, setForm] = useState({
    name: taxRate?.name ?? '',
    ratePercent: taxRate ? String((taxRate.rate * 100).toFixed(2)) : '',
    province: taxRate?.province ?? '',
    isDefault: taxRate?.isDefault ?? false,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const rateDecimal = parseFloat(form.ratePercent) / 100;

    if (taxRate) {
      onSave({
        name: form.name,
        rate: rateDecimal,
        province: form.province || null,
        is_default: form.isDefault,
      } as UpdateTaxRatePayload);
    } else {
      onSave({
        name: form.name,
        rate: rateDecimal,
        province: form.province || null,
        is_default: form.isDefault,
      } as CreateTaxRatePayload);
    }
  };

  const isRateValid =
    form.ratePercent !== '' &&
    !isNaN(parseFloat(form.ratePercent)) &&
    parseFloat(form.ratePercent) >= 0 &&
    parseFloat(form.ratePercent) <= 100;

  return (
    <Modal open onClose={onClose} title={taxRate ? 'Edit Tax Rate' : 'New Tax Rate'} size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Name"
          value={form.name}
          onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
          required
          maxLength={100}
          placeholder="e.g., HST, GST, PST"
        />
        <Input
          label="Rate (%)"
          type="number"
          value={form.ratePercent}
          onChange={(e) => setForm((prev) => ({ ...prev, ratePercent: e.target.value }))}
          required
          min={0}
          max={100}
          step="0.01"
          placeholder="e.g., 13.00"
          hint="Enter the tax rate as a percentage (0–100)"
        />
        <Select
          label="Province"
          value={form.province}
          onChange={(e) => setForm((prev) => ({ ...prev, province: e.target.value }))}
          options={PROVINCE_OPTIONS}
          hint="Leave blank for a global (all provinces) rate"
        />
        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
          <input
            type="checkbox"
            checked={form.isDefault}
            onChange={(e) => setForm((prev) => ({ ...prev, isDefault: e.target.checked }))}
            className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
          />
          Set as default rate for this province
        </label>

        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={isSaving} disabled={!form.name.trim() || !isRateValid}>
            {taxRate ? 'Update' : 'Create'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
